import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Theme,
    Typography,
    Tooltip,
    alpha,
    Box,
    Checkbox,
    Divider,
    InputBase,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    TextField,
    Toolbar,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import MoreIcon from "@mui/icons-material/More";
import MailIcon from "@mui/icons-material/Mail";
import { LoadingButton } from "@mui/lab";
import { visuallyHidden } from '@mui/utils';
import SearchIcon from '@mui/icons-material/Search';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import FilterListIcon from "@mui/icons-material/FilterList"

import { AxiosError } from "axios";
import moment from "moment";
import React, { useEffect, useRef, useState, Dispatch } from "react";
import { useSelector } from "react-redux";

import {
    getRequestDemos,
    getRequestDemo,
    postApproveDemo,
    sendEmail,
    generateRequestDemoEmail,
    verifyCenterEmail,
} from "../api";
import RequestDemoTable from "../components/RequestDemoTable";
import SimpleSnackBar, { SnackBarProp } from "../components/SimpleSnackBar";
import { AppState } from "../reducers";
import {
    StyledCircularProgress,
    StyledContainer,
    StyledGridRow,
} from "../StyledComponents";
import { ApproveStatus, RequestDemo } from "../types/RequestDemo";
import { RoleType } from "../types/UserRole";
import { firebaseUser } from "../firebase";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

interface LoadedData {
  kind: "loaded";
  data: RequestDemo[];
}
  
interface LoadingData {
  kind: "loading";
}
  
interface LoadingDataError {
  kind: "error";
  error?: string;
}
  
type LoadableData = LoadedData | LoadingData | LoadingDataError;
  
export default function ApproveDemoPage(props: { setTitle: Dispatch<React.SetStateAction<string>>}) {
  const {t} = useTranslation();

  const closeErrorSnackBar = () => {
      setSnackBar({ ...snackBar, open: false });
  };

  const [requestDemos, setRequestDemos] = useState<LoadableData>();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [snackBar, setSnackBar] = useState<SnackBarProp>({
    open: false,
    onClose: closeErrorSnackBar,
    message: "",
    variant: undefined,
  });

  const onApproveButtonClick = (id: string | undefined) => {
    if (id !== undefined) {
      setIsSubmitting(true);
      postApproveDemo(id)
        .then((data) => {
          verifyCenterEmail(id);
          console.log("ok");
          setSnackBar({
            ...snackBar,
            message: t("msg.approved"),
            open: true,
            variant: "success",
          });
        })
        .catch((error: AxiosError) => {
          console.log(error);
          if (error.response) {
            if (error.response.status === 500) {
              setSnackBar({
                ...snackBar,
                message:t("msg.approve_demo_backend"),
                open: true,
                variant: "error",
              });
            } else {
              setSnackBar({
                ...snackBar,
                message: (error as any).response.data.message,
                open: true,
                variant: "warning",
              });
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
          _getRequestDemos();
        });
    }
  };

  const onResendButtonClick = async (id: string | undefined) => {
    if (id !== undefined) {
      setIsSubmitting(true);
      if (requestDemos !== undefined && requestDemos.kind === "loaded") {
        const temp = requestDemos.data.filter((x) => x.id === id)[0];
        if (temp.is_approved === false) {
          try {
            await sendEmail(generateRequestDemoEmail(temp.email, temp.name));
            setSnackBar({
              ...snackBar,
              message: t("msg.approve_demo_app_email_resent"),
              open: true,
              variant: "success",
            });
          } catch (e: any) {
            if (e.response) {
              if (e.response.status === 500) {
                setSnackBar({
                  ...snackBar,
                  message:t("msg.approve_demo_backend"),
                  open: true,
                  variant: "error",
                });
              } else {
                setSnackBar({
                  ...snackBar,
                  message: e.response.data.message,
                  open: true,
                  variant: "warning",
                });
              }
            } else {
              setSnackBar({
                ...snackBar,
                message: t("msg.network_issue"),
                open: true,
                variant: "warning",
              });
            }
          }
        } else if (temp.is_approved === true && temp.created_user_id === 0) {
          try {
            await postApproveDemo(id);
            setSnackBar({
              ...snackBar,
              message: t ("msg.approve_demo_confirm_email_resent"),
              open: true,
              variant: "success",
            });
          } catch (e: any) {
            if (e.response) {
              if (e.response.status === 500) {
                setSnackBar({
                  ...snackBar,
                  message:t("msg.approve_demo_backend"),
                  open: true,
                  variant: "error",
                });

              } else {
                setSnackBar({
                  ...snackBar,
                  message: e.response.message,
                  open: true,
                  variant: "warning",
                });
              }
            } else {
              setSnackBar({
                ...snackBar,
                message: t("msg.network_issue"),
                open: true,
                variant: "warning",
              });
            }
          }
        }
      }
      setIsSubmitting(false);
    }
  };

  const loggedInUser = useSelector<AppState, firebaseUser | null>( state => state.loggedInUser );

  const [requestModalOpen, setRequestModalOpen] = useState<boolean>(false);

  const [selectedRequestDemo, setSelectedRequestDemo] = useState<RequestDemo>();

  const [editing, setEditing] = useState<boolean>(false);

  const loggedUserRole = useSelector<AppState, RoleType | null>( state => state.userRole );

  useEffect(() => {
    _getRequestDemos();
    props.setTitle("approveDemo");
  }, []);

  const _getRequestDemos = async () => {
    if (requestDemos === undefined || requestDemos.kind !== "loaded") {
      setRequestDemos({ kind: "loading" });
    }
    try {
      const data = await getRequestDemos();
      if (data !== undefined) {
        let temp = data.map((x) => {
          if (x.is_approved === false) {
            return { ...x, approveStatus: "waiting" as ApproveStatus };
          } else if (x.is_approved === true && x.created_user_at === 0) {
            return { ...x, approveStatus: "approved" as ApproveStatus };
          } else {
            return { ...x, approveStatus: "registered" as ApproveStatus };
          }
        });
        temp = temp.map((x) => {
          let created_at_x = moment.utc(x.created_at)
          if (!(created_at_x.isValid())) {
            return {
              ...x,
              created_at: created_at_x
                .local()
                .format("YYYY-MM-DD HH:mm:ss")
            }
          }
          return {
            ...x,
            created_at: moment
              .utc(x.created_at)
              .local()
              .format("YYYY-MM-DD HH:mm:ss"),
          };
        });
        setRequestDemos({ kind: "loaded", data: temp });
      }
    } catch (e: any) {
      setRequestDemos({ kind: "error", error: e });
    }
  };

  useEffect(() => {
    console.log(requestDemos);
  }, [requestDemos]);

  const handleRequestModalOpen = async (data: RequestDemo | undefined) => {
    if (data !== undefined && data.id !== undefined) {
      const temp = await getRequestDemo(data.id);
      setRequestModalOpen(true);
      if (temp != null) {
        setSelectedRequestDemo({ ...temp, id: data.id });
      }
    }
  };

  const handleRequestModalClose = () => {
    setRequestModalOpen(false);
    setEditing(false);
    _getRequestDemos();
  };

  const handleEdit = () => {
    setEditing(true);
  };

  // Table States
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("UID");
  const [tableDatas, setTableDatas] = useState<RequestDemo[]>([]);
  const [tableTempDatas, setTableTempDatas] = useState<RequestDemo[]>([]);
  const [allProfiles, setAllProfiles] = useState<RequestDemo[]>([]);

  useEffect(() => {
    if (requestDemos && requestDemos.kind === "loaded") {
      setTableDatas(requestDemos.data);
      setTableTempDatas(requestDemos.data);
      setAllProfiles(requestDemos.data);
    }
  }, [requestDemos])

  useEffect(() => {
    handleDataFiltering();
  }, [allProfiles])

  // Table functions
  const handleChangePage = (e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  const handleRequestSort = (e: unknown, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }

  // filtered: determine the element to show or not to show
  // roleValid: check user role whether the user have permission to view the specific element
  const [columns, setColumns] = useState<Column[]>([
    { id: "name", minWidth: 150, filtered: false },
    { id: "email", minWidth: 150, filtered: false },
    { id: "occupation", minWidth: 100, filtered: false },
    { id: "company_name", minWidth: 150, filtered: false },
    { id: "contact_number", minWidth: 150, filtered: false },
    { id: "company_address", minWidth: 150, filtered: false },
    { id: "created_at", minWidth: 200, filtered: false },
  ])
  const [allColumn, setAllColumn] = useState<boolean>(false);

  // Column Filter functions
  const [columnFilterOpen, setColumnFilterOpen] = useState<boolean>(false);
  const anchorColumnRef = useRef<any>(null);
  const prevColumnFilterOpen = useRef<boolean>(columnFilterOpen);
  
  const handleColumnFilterToggle = () => {
    setColumnFilterOpen((prevColumnFilterOpen: boolean) => !prevColumnFilterOpen);
  };

  const handleColumnFilterClose = (e: any) => {
    if (anchorColumnRef.current && anchorColumnRef.current.contains(e.target)) return;
    setColumnFilterOpen(false);
  }

  const handleColumnFilter = (id: string) => {
    if (id === "all") {
      setAllColumn(!allColumn);
      setColumns(
        columns.map( item => {
          if (item.id === "name") { return item; }
          return {
            ...item,
            filtered: allColumn,
          }
        })
      )
    } else {
      if (allColumn) setAllColumn(false);
      setColumns(
        columns.map( item => {
          if (item.id !== id) {
            return {
              ...item,
            }
          }
          return {
            ...item,
            filtered: !item.filtered,
          }
        })
      )
    }
  }

  function handleListKeyDown(e: any) {
    if (e.key === "Tab") {
      e.preventDefault();
      setColumnFilterOpen(false);
      setDataFilterOpen(false);
    } else if (e.key === "Escape") {
      setColumnFilterOpen(false);
      setDataFilterOpen(false);
    }
  }

  useEffect(() => {
    if (prevColumnFilterOpen.current === true && columnFilterOpen === false) {
      anchorColumnRef.current.focus();
    }
    prevColumnFilterOpen.current = columnFilterOpen;
  }, [columnFilterOpen]);

  // Search Function
  function handleSearch(e: any) {
    let showProfiles: RequestDemo[] = [];

    if (e.target.value && e.target.value.replace(/\s/g, "").length) {
      let target: string = e.target.value.replace(/\s/g, "")
      tableTempDatas.filter(doc => 
        doc.company_name?.replace(/\s/g, "").includes(target) ||
        doc.company_address?.replace(/\s/g, "").includes(target) ||
        doc.contact_number?.includes(target) ||
        doc.email?.includes(target) ||
        doc.name?.replace(/\s/g, "").includes(target) || 
        doc.occupation?.replace(/\s/g, "").includes(target) ||
        doc.created_at?.toString().includes(target)
      ).forEach(doc => showProfiles.push(doc));
    } else {
      showProfiles = tableTempDatas;
    }

    setTableDatas(showProfiles);
    setPage(0);
  }

  // Data Filter functions
  const [dataFilterOpen, setDataFilterOpen] = useState<boolean>(false);
  const [dataFilterAttributes, setDataFilterAttributes] = useState<DataFilter[]>([
    { id: "occupation", value: "all" },
    { id: "company_name", value: "all" },
    { id: "contact_number", value: "all" },
    { id: "company_address", value: "all" },
    { id: "created_at", value: "all", boundary: ">" },
  ]);
  const [isAll, setIsAll] = useState<boolean>(true);
  const anchorDataRef = useRef<any>(null);
  const prevDataFilterOpen = useRef<boolean>(dataFilterOpen);

  function handleDataFilterToggle() {
    setDataFilterOpen((prevDataFilterOpen: boolean) => !prevDataFilterOpen);
  }

  const handleDataFilterClose = (e: any) => {
    if (anchorDataRef.current && (anchorDataRef.current.contains(e.target))) return;
    setDataFilterOpen(false);
  }

  const handleDataFilter = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    setDataFilterAttributes(dataFilterAttributes.map(item => {
      return (id !== item.id)? { ...item } : { ...item, value: e.target.value };
    }))
  }

  const handleDataInequality = (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    setDataFilterAttributes(dataFilterAttributes.map(item => {
      return (id !== item.id)? { ...item } : { ...item, boundary: e.target.value };
    }))
  }

  const handleDataFiltering = () => {
    let showProfiles: RequestDemo[] = allProfiles;

    setIsAll(dataFilterAttributes.every(item => item.value === "all"));
    
    dataFilterAttributes.forEach(item => {
      let date = new Date();
      let month = date.getMonth() + 1;
      let day = (date.getDate()/10) < 1? "0"+date.getDate() : date.getDate();

      if (item.value !== "all") {
        if (item.id === "occupation" || item.id === "contact_number" || item.id === "company_address") {
          showProfiles = showProfiles.filter(doc => item.value === "with"? doc[item.id] : !doc[item.id]);
        }

        if (item.id === "created_at") {
          let periodVal: number = parseInt(item.value, 10);
          let isYear = item.value.includes("年");
          let isMonthUnderFlow = month < periodVal;
          month = (isYear)? month : (isMonthUnderFlow)? (12 + month - periodVal) : (month - periodVal);
          let year = (isYear)? date.getFullYear() - periodVal : (isMonthUnderFlow)? (date.getFullYear() - 1) : date.getFullYear();
          let stringMonth = ((month/10) < 1? "0" : "") + month;
          let targetDate = year+"-"+stringMonth+"-"+day;

          let isWithin = item.boundary === "<";
          showProfiles = showProfiles.filter(doc => doc.created_at !== "Invalid date" && (doc.created_at? (isWithin)? targetDate < doc.created_at : targetDate > doc.created_at : false))
        }
      }
    })
    setTableDatas(showProfiles);
    setTableTempDatas(showProfiles);
    setPage(0);
  }

  useEffect(() => {
    handleDataFiltering();
  }, [dataFilterAttributes])

  useEffect(() => {
    if (prevDataFilterOpen.current === true && dataFilterOpen === false) {
      anchorDataRef.current.focus();
    }
    prevDataFilterOpen.current = dataFilterOpen;
  }, [dataFilterOpen]);

  const dataAttributes: DataAttributes[] = [
    { 
      id: "occupation",
      values: [
        { id: "all_occupation", value: "all" },
        { id: "with_occupation", value: "with" },
        { id: "without_occupation", value: "without" }
      ] 
    },
    { 
      id: "contact_number",
      values: [
        { id: "all_contact_number", value: "all" },
        { id: "with_contact_number", value: "with" },
        { id: "without_contact_number", value: "without" }
      ] 
    },
    { 
      id: "company_address",
      values: [
        { id: "all_company_address", value: "all" },
        { id: "with_company_address", value: "with" },
        { id: "without_company_address", value: "without" }
      ] 
    },
    { 
      id: "created_at",
      values: [
        { id: "all_created", label: "all", value: "all" },
        { id: "5_y_created", label: `5 `, value: "5y" },
        { id: "3_y_created", label: `3 `, value: "3y" },
        { id: "1_y_created", label: `1 `, value: "1y" },
        { id: "6_m_created", label: `6 `, value: "6m" },
        { id: "3_m_created", label: `3 `, value: "3m" }
      ],
      boundary: [
        { id: "gt", label: "comparison.before", values: ">" },
        { id: "lt", label: "comparison.within", values: "<"},
      ]
    },
  ]
  
  return (
    <div>
      <SimpleSnackBar
        open={snackBar.open}
        onClose={closeErrorSnackBar}
        message={snackBar.message}
        variant={snackBar.variant}
      />
      {loggedInUser && loggedUserRole === "admin" ? (
        <StyledContainer maxWidth="lg" style={{ paddingTop: 90, paddingBottom: 80, maxWidth: "100%", maxHeight: "80%"  }}>
          {requestDemos !== undefined && requestDemos.kind === "loading" && (
            <StyledCircularProgress />
          )}
          {requestDemos !== undefined && requestDemos.kind === "loaded" && (
            <div>
              <Toolbar
                sx={{
                  pl: { sm: 2 },
                  pr: { xs: 1, sm: 1 },
                  justifyContent: "right",
                }}
              >
                <div style={{ display: "inline-flex", justifyContent: "right" }}>
                  <Tooltip title={t("table_components.search")}>
                    <Search sx={{ zIndex: 3 }}>
                      <StyledIconWrapper>
                        <SearchIcon/>
                      </StyledIconWrapper>
                      <StyledInputBase
                        inputProps={{ "aria-label": "search" }}
                        onChange={(e) => handleSearch(e)}
                      />
                    </Search>
                  </Tooltip>
                  <Divider sx={{ height: "auto", m: 1 }} orientation="vertical" />
                  <Tooltip title={t("table_components.columnFilter")}>
                    <IconButton
                      ref={anchorColumnRef}
                      id="columnFilterButt"
                      aria-controls={columnFilterOpen ? "column-composition-menu" : undefined}
                      aria-expanded={columnFilterOpen ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={handleColumnFilterToggle}
                    >
                      <AlignHorizontalLeftIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    key="column-filter-menu"
                    id="column-composition-menu"
                    anchorEl={anchorColumnRef.current}
                    onKeyDown={handleListKeyDown}
                    open={columnFilterOpen}
                    onClose={handleColumnFilterClose}
                  >
                    <MenuList
                      id="column-composition-menu"
                      aria-labelledby="composition-button"
                      onKeyDown={handleListKeyDown}
                    >
                      <MenuItem key={`column-filter-all`} sx={{ padding: 0, paddingInline: 1, width: "100%", zIndex: 2 }} onClick={() => handleColumnFilter("all")}>
                        <Checkbox value="all" checked={allColumn}/>
                        All
                      </MenuItem>
                      {
                        columns.map((col) =>
                          (col.id !== "name" && (
                            <MenuItem key={`column-filter-${col.id}`} sx={{ padding: 0, paddingInline: 1, width: "100%", zIndex: 2 }} onClick={() => handleColumnFilter(col.id)}>
                              <Checkbox
                                value={col.id}
                                checked={!col.filtered}
                              />
                              {t(`common.${col.id}` as unknown as TemplateStringsArray)}
                            </MenuItem>
                        )))
                      }
                    </MenuList>
                  </Menu>
                  <Tooltip title={t("table_components.dataFilter")}>
                    <IconButton
                      ref={anchorDataRef}
                      id="dataFilterButt"
                      aria-controls={dataFilterOpen ? "data-composition-menu" : undefined}
                      aria-expanded={dataFilterOpen ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={handleDataFilterToggle}
                      color={!isAll? "warning" : "default"}
                    >
                      <FilterListIcon/>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    key={`data-filter-menu`}
                    id="data-composition-menu"
                    anchorEl={anchorDataRef.current}
                    onKeyDown={handleListKeyDown}
                    open={dataFilterOpen}
                    onClose={handleDataFilterClose}
                  >
                      {
                        dataAttributes.map((item) => {
                          if (item.id === "created_at") {
                            let isShowAll = dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value === "all";
                            return (
                              <div 
                                style={{ width: 200, display: "flex" }} 
                                key={`data-filter-${item.id}`}
                              >
                                <TextField
                                  select
                                  label={t(`common.${item.id}` as unknown as TemplateStringsArray)}
                                  value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataFilter(e, item.id)}
                                  variant="standard"
                                  key={`data-filter-${item.id}-value`}
                                  sx={{ display: "inline-flex", margin: 1, width: (isShowAll)? "100%" : "50%" }}
                                >
                                  {
                                    item.values.map(item_val => (
                                      <MenuItem key={`data-filter-${item.id}-value=${item_val.id}`} value={item_val.label}>
                                        {
                                          item_val.value === "all"? 
                                            t("approve_demo_page.all")
                                          : item_val.value.includes("y")? 
                                              item_val.label + t("units_date.year")
                                            : item_val.label + t("units_date.piece") + t("units_date.month")
                                        }
                                      </MenuItem>
                                    ))
                                  }
                                </TextField>
                                {
                                  (!isShowAll) && (
                                    <TextField
                                      select
                                      label=" "
                                      value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.boundary}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataInequality(e, item.id)}
                                      variant="standard"
                                      key={`data-filter-${item.id}-inequality`}
                                      sx={{ display: "inline-flex", margin: 1, width: "50%" }}
                                    >
                                      {
                                        item.boundary?.map(sign => (
                                          <MenuItem key={`data-filter-${item.id}-inequality-${sign.id}`} sx={{ padding: 1 }} value={sign.values}>
                                            {t(`${sign.label}` as unknown as TemplateStringsArray)}
                                          </MenuItem>
                                        ))
                                      }
                                    </TextField>
                                  )
                                }
                              </div>
                            )
                          } 
                          return (
                            <TextField
                              select
                              label={t(`common.${item.id}` as unknown as TemplateStringsArray)}
                              value={dataFilterAttributes.find( currentItem => currentItem.id === item.id )?.value}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDataFilter(e, item.id)}
                              variant="standard"
                              key={`data-filter-${item.id}`}
                              sx={{ display: "flex", margin: 1, width: "auto" }}
                            >
                              {
                                item.values.map(item_val => (
                                  <MenuItem key={`data-filter-${item.id}-value=${item_val.id}`} value={item_val.value}>
                                    {t(`approve_demo_page.${item_val.value}` as unknown as TemplateStringsArray)}
                                  </MenuItem>
                                ))
                              }
                            </TextField>
                          )
                        })
                      }
                  </Menu>
                </div>
              </Toolbar>
              <Paper sx={{ width: "100%", zIndex: -1 }}>
                <TableContainer  sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader aria-label="sticky table" sx={{ maxHeight: "80vh"}}>
                    <CustomHead
                      order={order}
                      orderBy={orderBy}
                      columns={columns}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      <TableRow />
                      {
                        tableDatas
                          .sort(getComparator(order, orderBy))
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map(thisUserRequest => {
                            return (
                              <CustomRow
                                key={`user-${thisUserRequest.email}-${thisUserRequest.created_user_at}`}
                                userRequest={thisUserRequest}
                                isSubmitting={isSubmitting}
                                columns={columns}
                                handleRequestModalOpen={handleRequestModalOpen}
                                onApproveButtonClick={onApproveButtonClick}
                                onResendButtonClick={onResendButtonClick}
                              />
                            )
                          })
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 30, 50, 100]}
                  component="div"
                  count={tableDatas.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("common.rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.more_than")}${to}}`}`
                  }
                />
              </Paper>
            </div>
          )}
          <Dialog
            scroll="body"
            onClose={handleRequestModalClose}
            aria-labelledby="customized-dialog-title"
            open={requestModalOpen}
            fullWidth
          >
            {editing === false ? (
              <>
                <DialogTitle id="customized-dialog-title">
                  {t("approve_demo_page.applicant_info")}
                  <IconButton
                    style={{ float: "right" }}
                    edge="start"
                    color="inherit"
                    onClick={handleEdit}
                    aria-label="close"
                  >
                    <EditIcon />
                  </IconButton>
                </DialogTitle>
                {selectedRequestDemo !== undefined && (
                  <DialogContent>
                    <StyledGridRow>
                      {t("approve_demo_page.contact_person_name")}：{selectedRequestDemo.name}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.referralCode")}：{selectedRequestDemo.referralCode}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.displayName")}：{selectedRequestDemo.email}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.contact_number")}：{selectedRequestDemo.contact_number}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.center_name")}：{selectedRequestDemo.company_name}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.center_address")}：{selectedRequestDemo.company_address}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.center_type")}：{t(`requestDemoTable.comp_type_${selectedRequestDemo.company_type}` as unknown as TemplateStringsArray)}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.center_type_specified")}：{selectedRequestDemo.company_type_other}
                    </StyledGridRow>
                    <StyledGridRow>
                      {t("common.center_ppl")}：{t(`requestDemoTable.${selectedRequestDemo.no_of_user}` as unknown as TemplateStringsArray)}
                    </StyledGridRow>
                    {selectedRequestDemo.users_age_distribution !==
                      undefined && (
                        <>
                          <StyledGridRow>{t("common.member_age")}：</StyledGridRow>
                          <StyledGridRow>
                            55-65:{" "}
                            {selectedRequestDemo.users_age_distribution.group_55_65
                              ? selectedRequestDemo.users_age_distribution.group_55_65
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                          65-75:{" "}
                            {selectedRequestDemo.users_age_distribution.group_65_75
                              ? selectedRequestDemo.users_age_distribution.group_65_75
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                          75-85:{" "}
                            {selectedRequestDemo.users_age_distribution.group_75_85
                              ? selectedRequestDemo.users_age_distribution.group_75_85
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                          {t("requestDemoTable.label_member_birth_above")}:{" "}
                            {selectedRequestDemo.users_age_distribution.group_85_above
                              ? selectedRequestDemo.users_age_distribution.group_85_above
                              : 0}
                          %
                        </StyledGridRow>
                        </>
                      )}
                    {selectedRequestDemo.users_mental_health_distribution !==
                      undefined && (
                        <>
                          <StyledGridRow>{t("requestDemoTable.label_member_mental_health")}：</StyledGridRow>
                          <StyledGridRow>
                            {t("requestDemoTable.label_healthy")}：
                          {selectedRequestDemo.users_mental_health_distribution.healthy
                              ? selectedRequestDemo.users_mental_health_distribution.healthy
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                            {t("requestDemoTable.label_health_suspect")}：
                          {selectedRequestDemo.users_mental_health_distribution.suspect
                              ? selectedRequestDemo.users_mental_health_distribution.suspect
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                            {t("requestDemoTable.label_health_early")}：
                          {selectedRequestDemo.users_mental_health_distribution.early
                              ? selectedRequestDemo.users_mental_health_distribution.early
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                          </StyledGridRow>
                          <StyledGridRow>
                            {t("requestDemoTable.label_health_middle")}：
                          {selectedRequestDemo.users_mental_health_distribution.middle
                              ? selectedRequestDemo.users_mental_health_distribution.middle
                              : 0}
                          %<span style={{ paddingRight: 15 }} />
                            {t("requestDemoTable.label_health_late")}：
                          {selectedRequestDemo.users_mental_health_distribution.late
                              ? selectedRequestDemo.users_mental_health_distribution.late
                              : 0}
                          %
                        </StyledGridRow>
                        </>
                      )}
                    {/* <StyledGridRow>
                      已有服務：{selectedRequestDemo.current_services}
                    </StyledGridRow>
                    <StyledGridRow>
                      未來服務計劃：{selectedRequestDemo.future_services}
                    </StyledGridRow>
                    <StyledGridRow>
                      資助形式：{selectedRequestDemo.funding_source}
                    </StyledGridRow> */}
                    <StyledGridRow>
                      {t("requestDemoTable.label_submission_time")}：
                      {moment
                        .utc(selectedRequestDemo.created_at)
                        .local()
                        .format("YYYY-MM-DD HH:mm:ss") === "Invalid date" ? 
                        t("requestDemoTable.invalid_date") : 
                        moment
                        .utc(selectedRequestDemo.created_at)
                        .local()
                        .format("YYYY-MM-DD HH:mm:ss")}
                    </StyledGridRow>
                    <StyledGridRow>
                      {selectedRequestDemo.is_approved
                        ? selectedRequestDemo.created_user_at
                          ? t("requestDemoTable.label_applied_at") +
                          moment
                            .utc(selectedRequestDemo.created_user_at)
                            .local()
                            .format("YYYY-MM-DD HH:mm:ss")
                          : t("requestDemoTable.label_approved_but_not_applied")
                        : t("requestDemoTable.label_not_approved")}
                    </StyledGridRow>
                  </DialogContent>
                )}
              </>
            ) : (
              selectedRequestDemo !== undefined && (
                <RequestDemoTable
                  data={selectedRequestDemo}
                  onClose={handleRequestModalClose}
                />
              )
            )}
          </Dialog>
        </StyledContainer>
      ) : (
        <StyledContainer maxWidth="lg">
          <SimpleSnackBar
            open={true}
            onClose={closeErrorSnackBar}
            message={t("msg.no_permission_to_view")}
            variant="warning"
            autoClose={99999}
          />
        </StyledContainer>
      )}
    </div>
  );
}

//// Custom Table ////
const StyledTableCell = styled(TableCell)({
  padding: 14,
})

interface Column {
  id:
    "name" | 
    "email" | 
    "occupation" | 
    "company_name" |
    "contact_number" |
    "company_address" |
    "created_at";
  filtered?: boolean;
  minWidth?: number;
  align?: "left";
  format?: (value: number) => string;
}

interface DataFilter {
  id: 
    "name" | 
    "email" | 
    "occupation" | 
    "company_name" |
    "contact_number" |
    "company_address" |
    "created_at";
  value: string;
  boundary?: string;
}

interface DataAttributes {
  id:
    "occupation" |
    "contact_number" |
    "company_address" |
    "created_at";
  label?: string;
  values: {
    id: string;
    label?: string;
    value: string;
  }[];
  boundary?: {
    id: 
      "gt" |
      "lt";
    label: string;
    values: 
      ">" |
      "<";
  }[];
}

function Comparator(a: any, b: any, orderBy: string) {
  return (b[orderBy] < a[orderBy] || !b[orderBy])? -1 : (b[orderBy] > a[orderBy] || !a[orderBy])? 1 : 0;
}

function getComparator(order: "asc" | "desc", orderBy: string) {
  return order === "desc"
    ? (a: any, b: any) => Comparator(a, b, orderBy)
    : (a: any, b: any) => -Comparator(a, b, orderBy);
}

function CustomRow(
  props: {
    columns: Column[],
    userRequest: RequestDemo,
    isSubmitting: boolean,
    handleRequestModalOpen: (data: RequestDemo) => void,
    onApproveButtonClick: (id: string | undefined) => void,
    onResendButtonClick: (id: string | undefined) => void,
  }
) {
  const {t} = useTranslation();

  const { 
    columns, 
    userRequest,
    isSubmitting,
    handleRequestModalOpen,
    onApproveButtonClick,
    onResendButtonClick,
  } = props;
  const user_name = userRequest.name;
  const user_created_at = userRequest.created_user_at;
  const user_key = `user-${user_name}-${user_created_at}`;
  const value: any = userRequest.approveStatus;

  const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }))(Tooltip);

  return (
    <TableRow hover sx={{ "& > *": {borderBottom: "unset" }}}>
      <StyledTableCell 
        key={`${user_key}-uid-cell`}
        id={user_name}
        component="th" 
        scope="row"
      >
        {user_name}
      </StyledTableCell>
      {
        columns.map(col => {
          if (col.filtered) {
            return;
          }
          if (col.id === "created_at") {
            return (
              <StyledTableCell id={user_name} key={`${user_key}-${col.id}-cell`} align="left">
                {/* {moment(userRequest[col.id]).format("YYYY-MM-DD HH:mm:ss")} */}
                {
                  moment
                  .utc(userRequest[col.id])
                  .local()
                  .format("YYYY-MM-DD HH:mm:ss") === "Invalid date" ? 
                  t("requestDemoTable.invalid_date") : 
                  moment
                  .utc(userRequest[col.id])
                  .local()
                  .format("YYYY-MM-DD HH:mm:ss")
                }
              </StyledTableCell>
            )
          }
          if (col.id !== "name") {
            return (
              <StyledTableCell id={user_name} key={`${user_key}-${col.id}-cell`} align="left">
                {userRequest[col.id]}
              </StyledTableCell>
            )
          }
        })
      }
      <StyledTableCell id={user_name} key={`${user_key}-${t("approve_demo_page.view_applicant_info")}-cell`} align="left">
        <Tooltip title={t("approve_demo_page.view_applicant_info")}>
          <IconButton
            onClick={() => handleRequestModalOpen(userRequest)}
            color="primary"
          >
            <MoreIcon />
          </IconButton>
        </Tooltip>
      </StyledTableCell>
      <StyledTableCell id={user_name} key={`${user_key}-approveStatus-cell`} align="left">
        {(value === "waiting" || value === "待准許") &&
          <LoadingButton 
            loading={isSubmitting} 
            onClick={() => {onApproveButtonClick(userRequest.id)}}
            color="primary"
            variant="contained"
          >
            {t("approve_demo_page.approve")}
          </LoadingButton>
        }
        {
          (value === "approved" || value === "已准許") &&
          t("requestDemoTable.label_approved_but_not_applied1")
        }
        {
          (value === "registered" || value === "已申請") ? (
            <HtmlTooltip
              title={
                <React.Fragment>
                  <Typography color="inherit">
                    {t("requestDemoTable.label_applied_at") +
                      moment
                        .utc(userRequest.created_at)
                        .local()
                        .format("YYYY-MM-DD HH:mm:ss")}
                  </Typography>
                </React.Fragment>
              }
            >
              <IconButton aria-label="delete">
                <CheckCircleOutlineIcon
                  style={{ color: "green" }}
                />
              </IconButton>
            </HtmlTooltip>
          ) : (
            <Tooltip title={t("requestDemoTable.label_resend_email")}>
              <LoadingButton
                loading={isSubmitting}
                // style={{ marginLeft: 10 }}
                onClick={() => {onResendButtonClick(userRequest.id)}}
              >
                <MailIcon color={(!isSubmitting)?"primary":"disabled"}/>
              </LoadingButton>
            </Tooltip>
          )
        }
      </StyledTableCell>
    </TableRow>
  )
}

function CustomHead(
  props: {
    order: "asc" | "desc" ,
    orderBy: string,
    columns: Column[],
    onRequestSort: (arg0: any, arg1: string) => void,
  }
) {

  const { order, orderBy, columns, onRequestSort } = props;
  const {t} = useTranslation();
  const createSortHandler = (property: string) => (e: any) => {
    onRequestSort(e, property);
  };

  return (
    <TableHead>
      <TableRow>
        {
          columns.map((col) => {
            return (
              (!col.filtered && (
                <StyledTableCell
                  key={col.id}
                  align={col?.align?? "left"}
                  style={{ minWidth: col.minWidth }}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={createSortHandler(col.id)}
                  >
                    {t(`common.${col.id}` as unknown as TemplateStringsArray)}
                    {
                      orderBy === col.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      )
                      : null
                    }
                  </TableSortLabel>
                </StyledTableCell>
            )))
          })
        }
        <StyledTableCell style={{ maxWidth: 20 }}/>
        <StyledTableCell sx={{ maxWidth: 20 }}>
          {t("requestDemoTable.application_status")}
        </StyledTableCell>
      </TableRow>
    </TableHead>
  )
}

const Search = styled("div")(({theme}) => ({
  position: "relative",
  marginLeft: 0,
  marginRight: 7,
  width: "100%",
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}))

const StyledIconWrapper = styled("div")(({theme}) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}))

const StyledInputBase = styled(InputBase)(({theme}) => ({
  "& .MuiInputBase-input": {
    padding: theme.spacing(1,1,1,0),
    borderRadius: "25px",
    paddingLeft: "3em",
    paddingRight: "0",
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up('sm')]: {
      width: 0,
      '&:hover': {
        width: '20ch',
        backgroundColor: alpha("#BBBBBB", 0.25),
      },
      '&:focus': {
        width: '20ch',
        backgroundColor: alpha("#BBBBBB", 0.25),
      },
    },
  }
}))

// Original Data Table
{/* <MUIDataTable
  title={""}
  data={requestDemos.data}
  columns={[
    {
      label: "姓名",
      name: "name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      label: "電郵",
      name: "email",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      label: "職位",
      name: "occupation",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      label: "公司名稱",
      name: "company_name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      label: "聯絡電話",
      name: "contact_number",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      label: "公司地址",
      name: "company_address",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      label: "提交時間",
      name: "created_at",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      label: " ",
      name: "id",
      options: {
        filter: false,
        sort: false,
        viewColumns: false,
        customBodyRender: (value) => (
          <Tooltip title="檢視申請詳情">
            <IconButton
              onClick={() =>
                handleRequestModalOpen(
                  requestDemos.data.find((x) => x.id === value)
                )
              }
              color="primary"
            >
              <MoreIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    },
    {
      label: "申請狀況",
      name: "approveStatus",
      options: {
        filter: true,
        sort: true,
        viewColumns: false,
        customBodyRender: (value, tableMeta) => {
          return (
            <>
              {value === "待准許" &&
              //   <ButtonWithLoading
              //     isLoading={isSubmitting}
              //     onClick={() => {
              //       const temp = requestDemos.data.find((x) => x.email === tableMeta.rowData[1]);
              //       if (temp !== undefined) {
              //         onApproveButtonClick(
              //           temp.id
              //         )
              //       }
              //     }
              //     }
              //     color="primary"
              //   >
              //     准許
              // </ButtonWithLoading>
              <LoadingButton 
                loading={isSubmitting} 
                onClick={() => {
                  const temp = requestDemos.data.find((x) => x.email === tableMeta.rowData[1]);
                  if (temp !== undefined) {
                    onApproveButtonClick(
                      temp.id
                    )
                  }
                }}
                color="primary"
                variant="contained"
              >
                准許
              </LoadingButton>
              }
              {value === "已准許" &&
                "已批准 (未申請帳戶）"}
              {value === "已申請" ? (
                <>
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                        <Typography color="inherit">
                          {"申請於 " +
                            moment
                              .utc(
                                tableMeta.rowData[6]
                              )
                              .local()
                              .format("YYYY-MM-DD HH:mm:ss")}
                        </Typography>
                      </React.Fragment>
                    }
                  >
                    <IconButton aria-label="delete">
                      <CheckCircleOutlineIcon
                        style={{ color: "green" }}
                      />
                    </IconButton>
                  </HtmlTooltip>
                </>
              ) : (
                <Tooltip title="重發電郵">
                  // <IconButtonWithLoading
                  //   isLoading={isSubmitting}
                  //   style={{ marginLeft: 10 }}
                  //   onClick={() => {
                  //     const temp = requestDemos.data.find((x) => x.email === tableMeta.rowData[1]);
                  //     if (temp !== undefined) {
                  //       onResendButtonClick(
                  //         temp.id
                  //       )
                  //     }
                  //   }}
                  // >
                  //   <MailIcon color="primary" />
                  // </IconButtonWithLoading>
                  <LoadingButton
                    loading={isSubmitting}
                    // style={{ marginLeft: 10 }}
                    onClick={() => {
                      const temp = requestDemos.data.find((x) => x.email === tableMeta.rowData[1]);
                      if (temp !== undefined) {
                        onResendButtonClick(
                          temp.id
                        )
                      }
                    }}
                  >
                    <MailIcon color={(!isSubmitting)?"primary":"disabled"}/>
                  </LoadingButton>
                </Tooltip>
              )}
            </>
          )
        },
      },
    },
  ]}
  options={{
    selectableRows: "none",
    elevation: 0,
    print: false,
    download: false,
  }}
/> */}